import React, { useRef, useState } from 'react';
import { useMessages, MessageTypes } from '@allie/mfe';
import {
  AllieAccordion,
  AllieAccordionGroup,
  AllieAlertCircleOutlineIcon,
  AllieButton,
  AllieCheckCircleIcon,
  AllieEyeCloseIcon,
  AllieEyeOpenIcon,
  AllieInput,
  AllieItem,
  AllieLabel,
  AllieText,
} from '@allie/app-ui-lib';
import { CreatePasswordContainer, ValidationItem } from './CreatePassword.styles';
import { CreateSuccess } from './components/CreateSuccess';
import { CreateLoading } from './components/CreateLoading';
import { useAllieAuth } from '../../hooks';
import { useAppTranslations } from 'src/hooks/useAppTranslations';

interface CreatePasswordProps {
  email: string;
  id: number;
}

const namespace = 'create-password';

export const CreatePassword: React.FC<CreatePasswordProps> = ({
  email,
  id,
}) => {
  const { changeEmailPassword } = useAllieAuth();
  const passwordRef = useRef<any>(null);
  const confirmPasswordRef = useRef<any>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState(null);
  const [hasMinLength, setHasMinLength] = useState(false);
  const [hasLowerUpper, setHasLowerUpper] = useState(false);
  const [hasNumber, setHasNumber] = useState(false);
  const [hasSymbol, setHasSymbol] = useState(false);
  const [isDisable, setIsDisable] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [changePasswordSuccess, setChangePasswordSuccess] = useState(false);
  const { showMessage } = useMessages();
  const {
    RESET_ACCORDION_CONTENT_PARAGRAPH_1,
    RESET_ACCORDION_CONTENT_PARAGRAPH_2,
    RESET_ACCORDION_TITLE,
    RESET_CONFIRM,
    RESET_CONFIRM_PASSWORD_PLACEHOLDER,
    CREATE_NEW_PASSWORD_TITLE,
    CREATE_NEW_PASSWORD_SUBTITLE,
    RESET_PASSWORD_PLACEHOLDER,
    RESET_VALIDATION_LOWER_UPPER,
    RESET_VALIDATION_MIN_LENGTH,
    RESET_VALIDATION_NUMBER,
    RESET_VALIDATION_SYMBOL,
    CREATE_NEW_PASSWORD_VALIDATION_TITLE,
    CREATE_NEW_PASSWORD_ERROR,
  } = useAppTranslations();

  const handleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const checkMinLength = () => {
    setHasMinLength(passwordRef?.current?.value.length >= 8);
  };

  const checkLowerUpper = () => {
    const validate = /(?=.*[A-Z])(?=.*[a-z])/;
    setHasLowerUpper(validate.test(passwordRef.current.value));
  };

  const checkNumber = () => {
    const validate = /(?=.*\d)/;
    setHasNumber(validate.test(passwordRef.current.value));
  };

  const checkSymbol = () => {
    const validate = /(?=.*[-+_!@#$%^&*.,?])/;
    setHasSymbol(validate.test(passwordRef.current.value));
  };

  const isMatched = () => {
    if (
      hasLowerUpper &&
      hasMinLength &&
      hasNumber &&
      hasSymbol &&
      passwordRef?.current?.value === confirmPasswordRef?.current?.value &&
      passwordRef.current.value.length > 0 &&
      confirmPasswordRef?.current?.value.length > 0
    ) {
      setIsDisable(false);
    } else {
      setIsDisable(true);
    }
  };
  const onPasswordChange = () => {
    isMatched();
    checkMinLength();
    checkLowerUpper();
    checkNumber();
    checkSymbol();
  };

  const onConfirmPasswordChange = () => {
    isMatched();
  };

  const onSubmit = async () => {
    setIsLoading(true);
    setPassword(confirmPasswordRef.current.value);

    changeEmailPassword(email, confirmPasswordRef.current.value, id)
    .then(() => { setChangePasswordSuccess(true); })
    .catch(() => { showMessage({ type: MessageTypes.ERROR, message: CREATE_NEW_PASSWORD_ERROR }) })
    .finally(() => { setIsLoading(false) });
  }

  return (
    <CreatePasswordContainer>
      {!changePasswordSuccess && !isLoading && (
        <div className={namespace}>
          <div style={{ textAlign: 'center' }}>
            <AllieText type="h3">{CREATE_NEW_PASSWORD_TITLE}</AllieText>
          </div>
          <div className={`${namespace}-body`}>
            <div className={`${namespace}-body-text`} style={{ marginTop: 16 }}>
              <AllieText type="h4" color="neutral-80">
                {CREATE_NEW_PASSWORD_SUBTITLE}
              </AllieText>
            </div>
            <AllieInput
              ref={passwordRef}
              placeholder={RESET_PASSWORD_PLACEHOLDER}
              type={showPassword ? 'text' : 'password'}
              corner="rounded-edges"
              clearInput={true}
              onChange={onPasswordChange}
              allieSize="small"
              icon={showPassword ? <AllieEyeOpenIcon /> : <AllieEyeCloseIcon />}
              onClick={handleShowPassword}
            />
            <div className={`${namespace}-body-text`}>
              <div style={{ marginTop: 12 }}>
                <AllieText type="body1" color="neutral-90" weight={600}>
                  {CREATE_NEW_PASSWORD_VALIDATION_TITLE}
                </AllieText>
              </div>
              <ValidationItem>
                <div
                  className={`${namespace}-input-validation-${hasMinLength ? 'success' : 'failed'}`}
                  >
                  <AllieCheckCircleIcon />
                  <AllieText type="body2" color="neutral-90">
                    {RESET_VALIDATION_MIN_LENGTH}
                  </AllieText>
                </div>
              </ValidationItem>
              <ValidationItem>
                <div
                  className={`${namespace}-input-validation-${
                    hasLowerUpper ? 'success' : 'failed'
                  }`}
                >
                  <AllieCheckCircleIcon />
                  <AllieText type="body2" color="neutral-90">
                    {RESET_VALIDATION_LOWER_UPPER}
                  </AllieText>
                </div>
              </ValidationItem>
              <ValidationItem>
                <div
                  className={`${namespace}-input-validation-${hasNumber ? 'success' : 'failed'}`}
                >
                  <AllieCheckCircleIcon />
                  <AllieText type="body2" color="neutral-90">
                    {RESET_VALIDATION_NUMBER}
                  </AllieText>
                </div>
              </ValidationItem>
              <ValidationItem>
                <div
                  className={`${namespace}-input-validation-${hasSymbol ? 'success' : 'failed'}`}
                >
                  <AllieCheckCircleIcon />
                  <AllieText type="body2" color="neutral-90">
                    {RESET_VALIDATION_SYMBOL}
                  </AllieText>
                </div>
              </ValidationItem>
            </div>
            <AllieInput
              ref={confirmPasswordRef}
              placeholder={RESET_CONFIRM_PASSWORD_PLACEHOLDER}
              type={showPassword ? 'text' : 'password'}
              corner="rounded-edges"
              clearInput={true}
              onChange={onConfirmPasswordChange}
              allieSize="small"
              icon={showPassword ? <AllieEyeOpenIcon /> : <AllieEyeCloseIcon />}
              onClick={handleShowPassword}
            />
          </div>
          <AllieAccordionGroup expand="inset" className={`${namespace}-accordion`}>
            <AllieAccordion>
              <AllieItem className={`${namespace}-accordion-header`} slot="header">
                <AllieAlertCircleOutlineIcon allieColor="success" />
                <AllieLabel>{RESET_ACCORDION_TITLE}</AllieLabel>
              </AllieItem>
              <div className={`${namespace}-accordion-content`} slot="content">
                <AllieText type="body2" color="neutral-40">
                  {RESET_ACCORDION_CONTENT_PARAGRAPH_1}
                  <br />
                  <br />
                  {RESET_ACCORDION_CONTENT_PARAGRAPH_2}
                </AllieText>
              </div>
            </AllieAccordion>
          </AllieAccordionGroup>
          <div className={`${namespace}-confirm`}>
            <AllieButton
              onClick={onSubmit}
              corner="rounded-edges"
              allieColor="primary"
              allieSize="small"
              disabled={isDisable}
            >
              {RESET_CONFIRM}
            </AllieButton>
          </div>
        </div>
      )}
      {changePasswordSuccess && password && (
        <CreateSuccess email={email} password={password} />
      )}
      {isLoading && <CreateLoading />}
    </CreatePasswordContainer>
  );
};
